import { useCallback, useEffect, useState } from 'react';
import componentsCache from '@atlassian/jira-cache/src/services/components/index.tsx';
import { useResource } from '@atlassian/react-resource-router';
import { projectGlobalComponentsResource } from '@atlassian/jira-router-resources-project-global-components/src/services/index.tsx';
import {
	type GlobalComponentsProperties,
	getGlobalComponentsProperty,
	setGlobalComponentsProperty,
} from './utils.tsx';

export function useGlobalComponentsProperty(projectKey: string) {
	const {
		data: resourceData,
		loading: resourceLoading,
		error: resourceError,
		promise: resourcePromise,
	} = useResource(projectGlobalComponentsResource);

	const [resourceAvailable] = useState<boolean>(
		Boolean(resourceData) ||
			Boolean(resourcePromise) ||
			Boolean(resourceError) ||
			Boolean(resourceLoading),
	);

	// If the resource is not available, fallback loading to true as per the old implementation
	const [loading, setLoading] = useState<boolean>(resourceAvailable ? resourceLoading : true);
	const [enabled, setEnabled] = useState<boolean | undefined>(
		resourceLoading === false ? resourceData?.value : undefined,
	);
	const [error, setError] = useState<Error | undefined>(
		resourceError instanceof Error ? resourceError : undefined,
	);

	const loadGlobalComponentsProperty = useCallback(() => {
		setEnabled(undefined);
		setError(undefined);
		setLoading(true);
	}, [setEnabled, setError, setLoading]);

	const resolveGlobalComponentsProperty = useCallback(
		(value: boolean | Error) => {
			if (value instanceof Error) {
				setEnabled(undefined);
				setError(value);
			} else {
				setEnabled(value);
				setError(undefined);
			}

			setLoading(false);
		},
		[setEnabled, setError, setLoading],
	);

	useEffect(() => {
		if (resourceLoading) {
			resourcePromise
				?.then((d) => resolveGlobalComponentsProperty(d?.value))
				.catch((e) => resolveGlobalComponentsProperty(e));
			// eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
		} else if (!resourceAvailable) {
			// If resource does not exist, we need to load the property manually
			loadGlobalComponentsProperty();
			getGlobalComponentsProperty(projectKey)
				.then((r: GlobalComponentsProperties) => resolveGlobalComponentsProperty(r.value))
				.catch((e) => resolveGlobalComponentsProperty(e));
		}
	}, [
		resourceLoading,
		resourcePromise,
		resolveGlobalComponentsProperty,
		loadGlobalComponentsProperty,
		projectKey,
		resourceAvailable,
	]);

	const setProperty = useCallback(
		(newValue: boolean) => {
			loadGlobalComponentsProperty();

			return (
				setGlobalComponentsProperty(projectKey, newValue)
					// We are clearing the Jira components cache here which is the
					// data source for "Recent components" in the components field
					// for Jira issues. This should be cleared when toggling between
					// global components enabled or disabled to avoid stales
					// suggestions from either.
					.then(() => componentsCache.clearAll())
					.then(() => resolveGlobalComponentsProperty(newValue))
					.catch((e) => {
						resolveGlobalComponentsProperty(e);
						throw e;
					})
			);
		},
		[loadGlobalComponentsProperty, resolveGlobalComponentsProperty, projectKey],
	);

	return {
		enabled,
		loading,
		error,
		setProperty,
	};
}
